import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Circle, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import { LocateFixed, Loader2, X } from "lucide-react";

const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
export const distM = (a, b) => {
  const h = Math.sin(rad(b[0] - a[0]) / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(rad(b[1] - a[1]) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
export const nearestOdp = (pos, odps) => odps.filter((o) => o.has_coords)
  .reduce((best, o) => { const d = distM(pos, [o.latitude, o.longitude]); return !best || d < best.d ? { odp: o, d } : best; }, null);
const fmtM = (m) => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`);

// Ikuti Posisi: watchPosition aktif sejak tombol ditekan sampai ditutup; ODP terdekat dihitung ulang tiap update.
export function useMyLocation(odps) {
  const [me, setMe] = useState(null);  // { loc, nearest, fitKey }
  const [locating, setLocating] = useState(false);
  const watch = useRef(null);
  const fixed = useRef(false);
  const odpRef = useRef(odps);
  odpRef.current = odps;
  const stop = () => { if (watch.current != null) navigator.geolocation.clearWatch(watch.current); watch.current = null; fixed.current = false; };
  useEffect(() => stop, []);
  const locate = () => {
    if (!navigator.geolocation) return toast.error("Perangkat/browser tidak mendukung lokasi.");
    if (watch.current != null) return setMe((m) => m && { ...m, fitKey: m.fitKey + 1 });  // sudah mengikuti → pusatkan ulang
    setLocating(true);
    watch.current = navigator.geolocation.watchPosition((p) => {
      const loc = { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy };
      fixed.current = true;
      setMe((m) => ({ loc, nearest: nearestOdp([loc.lat, loc.lng], odpRef.current), fitKey: m ? m.fitKey : 1 }));
      setLocating(false);
    }, (e) => {
      setLocating(false);
      if (e.code === 1) { stop(); setMe(null); return toast.error("Izin lokasi ditolak. Aktifkan izin lokasi untuk situs ini di browser."); }
      if (!fixed.current) { stop(); toast.error("Lokasi tidak dapat ditentukan. Coba lagi di area terbuka."); }  // setelah fix: abaikan gangguan sesaat
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 5000 });
  };
  const clear = () => { stop(); setMe(null); };
  return { me, locating, locate, clear };
}

export function MyLocationLayer({ loc, nearest, fitKey }) {
  const map = useMap();
  const last = useRef("");
  const nid = nearest?.odp.id;
  useEffect(() => {
    if (!loc) { last.current = ""; return; }
    const key = `${fitKey}|${nid}`;
    if (key !== last.current) {  // fix pertama, tombol Pusatkan, atau ODP terdekat berganti → tampilkan keduanya
      last.current = key;
      const pts = [[loc.lat, loc.lng], ...(nearest ? [[nearest.odp.latitude, nearest.odp.longitude]] : [])];
      if (pts.length > 1) map.flyToBounds(pts, { paddingTopLeft: [50, 150], paddingBottomRight: [50, 80], maxZoom: 18, duration: 0.8 });
      else map.flyTo(pts[0], 17, { duration: 0.8 });
    } else {  // saat berjalan: geser seperlunya agar titik tetap terlihat
      map.panInside([loc.lat, loc.lng], { paddingTopLeft: [50, 150], paddingBottomRight: [50, 80] });
    }
  }, [map, loc?.lat, loc?.lng, fitKey, nid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!loc) return null;
  const me = [loc.lat, loc.lng];
  return (
    <>
      {loc.acc > 0 && <Circle center={me} radius={loc.acc} interactive={false} pathOptions={{ color: "#2563EB", weight: 1, fillOpacity: 0.08 }} />}
      {nearest && <Polyline positions={[me, [nearest.odp.latitude, nearest.odp.longitude]]} interactive={false}
                            pathOptions={{ color: "#2563EB", weight: 2, dashArray: "6 6" }} />}
      <CircleMarker center={me} radius={8} pathOptions={{ color: "#fff", weight: 3, fillColor: "#2563EB", fillOpacity: 1 }}>
        <Tooltip permanent direction="top" offset={[0, -8]}>Lokasi Saya</Tooltip>
      </CircleMarker>
    </>
  );
}

const BTN = "flex items-center gap-1.5 rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-xs font-bold text-[#111827] shadow-md transition-colors hover:bg-[#F3F4F6] disabled:opacity-60";

export const MyLocationControl = ({ loc, nearest, busy, following, onLocate, onOpen, onClear }) => (
  <div className="absolute right-3 top-14 z-[450] flex max-w-[calc(100%-5rem)] flex-col items-end gap-2">
    <button type="button" className={BTN} onClick={onLocate} disabled={busy} data-testid="map-mylocation-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <LocateFixed className={`h-3.5 w-3.5 ${following ? "text-[#2563EB]" : ""}`} aria-hidden />}
      {following ? "Pusatkan" : "Lokasi Saya"}
    </button>
    {loc && (
      <div className="flex items-center gap-2 rounded-md border border-[#BFDBFE] bg-white px-2.5 py-1.5 text-xs shadow-md" data-testid="map-mylocation-info">
        <span className="relative flex h-2.5 w-2.5 shrink-0" title="Mengikuti posisi" data-testid="map-following-indicator">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2563EB] opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#2563EB]" />
        </span>
        {nearest ? (
          <button type="button" className="text-left" onClick={onOpen} data-testid="map-nearest-odp">
            <span className="text-[#6B7280]">ODP terdekat: </span><b className="font-mono text-[#0055FF] underline">{nearest.odp.kode}</b>
            <span className="whitespace-nowrap text-[#374151]" data-testid="map-nearest-distance"> · {fmtM(nearest.d)}</span>
          </button>
        ) : <span className="text-[#6B7280]" data-testid="map-nearest-none">Tidak ada ODP berkoordinat pada filter ini.</span>}
        <button type="button" onClick={onClear} aria-label="Tutup lokasi saya" className="text-[#6B7280] hover:text-[#111827]" data-testid="map-mylocation-clear"><X className="h-3.5 w-3.5" /></button>
      </div>
    )}
  </div>
);
